import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageService } from 'primeng/api';
import {
  InformacionEmpresaInfDetalle,
  InformacionEmpresaInfDetalleFiltro
} from 'src/app/domain/model/informacion-empresa/informacion-empresa-oc';
import { InformacionEmpresaRepository } from 'src/app/domain/service/informacion-empresa/informacion-empresa-repository';
import { OficinaRepository } from 'src/app/domain/service/oficina/oficina-repository';
import { DocumentoP8Service } from 'src/app/domain/service/documento-p8/documento-p8.service';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';
import {
  AsesorSeleccionado,
  BusquedaAsesorDialogComponent as AsesorSearchComponent
} from '../common/busquedas/busqueda-asesor-dialog.component';
import {
  BusquedaCentroTrabajoDialogComponent,
  CentroTrabajoSeleccionado
} from '../common/busquedas/busqueda-centro-trabajo-dialog.component';
import {
  BusquedaClasificacionDialogComponent,
  ClasificacionSeleccionada
} from '../common/busquedas/busqueda-clasificacion-dialog.component';
import {
  BusquedaTareaDialogComponent,
  TareaSeleccionada
} from '../common/busquedas/busqueda-tarea-dialog.component';

interface OpcionOficina {
  label: string;
  value: number;
}

interface OpcionCatalogo {
  label: string;
  value: number | null;
}

const TIPO_INFORME_OPTIONS: OpcionCatalogo[] = [
  { label: 'Seleccione tipo de informe', value: null },
  { label: 'FINAL', value: 1 },
  { label: 'PARCIAL', value: 2 }
];

const FUENTE_INGRESA_PENDIENTE = '1';
const CLASS_NAME_P8 = 'SeguimientoEmpresas';
const ORDER_P8 = 'FEFechaMovimiento ASC';

@Component({
  selector: 'app-informacion-empresa-detalle-informes-entregados',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ButtonModule,
    TableModule,
    InputTextModule,
    SelectModule,
    DatePickerModule,
    PaginatorModule,
    TooltipModule,
    ToastModule,
    ProgressSpinnerModule,
    BusquedaTareaDialogComponent,
    BusquedaClasificacionDialogComponent,
    BusquedaCentroTrabajoDialogComponent,
    AsesorSearchComponent
  ],
  providers: [MessageService],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  template: `
    <div class="p-3 sm:p-6 max-w-7xl mx-auto">
      <p-toast position="top-right"></p-toast>

      @if (descargandoDocumento()) {
        <div class="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center">
          <div class="bg-white rounded-lg p-6 shadow-xl flex flex-col items-center gap-4">
            <p-progressSpinner
              styleClass="w-16 h-16"
              strokeWidth="4"
              animationDuration=".8s">
            </p-progressSpinner>
            <span class="text-lg font-medium text-gray-700">{{ mensajeDescarga() }}</span>
            <span class="text-sm text-gray-500">Por favor espere</span>
          </div>
        </div>
      }

      <div class="mb-4 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <span class="text-sm text-gray-600 mb-2 flex items-center gap-2"
            >Información Empresa >> Informes Entregados >><b> Detalle</b>
          </span>
          <h1 class="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100">
            Detalle Informes Entregados
          </h1>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Consulta detallada de informes entregados por producto.
          </p>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <a [routerLink]="'/informacion-empresa'" [queryParams]="queryParamsPrincipal()">
            <button
              pButton
              label="Información Empresa"
              icon="pi pi-home"
              severity="secondary"
              class="p-button-outlined"
            ></button>
          </a>

          <a
            [routerLink]="'/informacion-empresa/informes-entregados'"
            [queryParams]="queryParamsRegreso()"
          >
            <button
              pButton
              label="Regresar"
              icon="pi pi-arrow-left"
              severity="secondary"
              class="p-button-outlined"
            ></button>
          </a>
        </div>
      </div>

      <div class="rounded-lg border border-orange-200 bg-orange-100 p-4 text-orange-900 mb-4">
        <div class="font-semibold mb-2">Contexto de consulta</div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <div class="rounded px-3 py-2 border border-orange-100">
            <span class="font-medium">Cliente:</span>
            <span class="ml-2">{{ nombreCliente() }}</span>
          </div>
          <div class="rounded px-3 py-2 border border-orange-100">
            <span class="font-medium">Producto:</span>
            <span class="ml-2">{{ productoContexto() }}</span>
          </div>
        </div>
      </div>

      <div class="border border-gray-200 dark:border-gray-700 rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800">
        <div class="font-medium text-gray-700 dark:text-gray-200 mb-3 text-sm">Filtros</div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Tarea</label>
            <div class="flex items-center gap-2 min-w-0 ">
              <input pInputText [value]="tareaSeleccionadaLabel()" class="flex-1 min-w-0" readonly />
              <button pButton icon="pi pi-search" class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]" pTooltip="Buscar tarea"
                (click)="mostrarBuscadorTarea = true"></button>
              @if (tareaSeleccionada()) {
                <button type="button" pButton icon="pi pi-times" class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                  severity="danger" pTooltip="Limpiar tarea" (click)="limpiarTarea()"></button>
              }
            </div>
          </div>

          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Clasificación</label>
            <div class="flex items-center gap-2 min-w-0 ">
              <input pInputText [value]="clasificacionSeleccionadaLabel()" class="flex-1 min-w-0" readonly />
              <button pButton icon="pi pi-search" class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]" pTooltip="Buscar clasificación"
                (click)="mostrarBuscadorClasificacion = true"></button>
              @if (clasificacionSeleccionada()) {
                <button type="button" pButton icon="pi pi-times" class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                  severity="danger" pTooltip="Limpiar clasificación" (click)="limpiarClasificacion()"></button>
              }
            </div>
          </div>

          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Tipo de Informe</label>
            <p-select
              [options]="tiposInforme()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="filtroTipoInforme"
              [showClear]="true"
              [style]="{ width: '100%' }"
              appendTo="body">
            </p-select>
          </div>

          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Consultor</label>
            <div class="flex items-center gap-2 min-w-0 ">
              <input pInputText [value]="consultorSeleccionadoLabel()" class="flex-1 min-w-0" readonly />
              <button pButton icon="pi pi-search" class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]" pTooltip="Buscar consultor"
                (click)="mostrarBuscadorConsultor = true"></button>
              @if (consultorSeleccionado()) {
                <button type="button" pButton icon="pi pi-times" class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                  severity="danger" pTooltip="Limpiar consultor" (click)="limpiarConsultor()"></button>
              }
            </div>
          </div>

          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Cumplimiento</label>
            <input
              pInputText
              [(ngModel)]="filtroCumplimiento"
              maxlength="11"
              class="w-full"
              placeholder="Ingrese cumplimiento"
              [readonly]="cumplimientoReadonly()"
              (input)="onCumplimientoInput($event)"
            />
          </div>

          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Oficina</label>
            <p-select
              [options]="oficinas()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="filtroOficina"
              [filter]="true"
              [showClear]="true"
              [style]="{ width: '100%' }"
              appendTo="body">
            </p-select>
          </div>

          <div class="md:col-span-2">
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Centro de Trabajo</label>
            <div class="flex items-center gap-2 min-w-0 ">
              <input pInputText [value]="centroTrabajoSeleccionadoLabel()" class="flex-1 min-w-0" readonly />
              <button pButton icon="pi pi-search" class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]" pTooltip="Buscar centro de trabajo"
                (click)="abrirBusquedaCentroTrabajo()"></button>
              @if (centroTrabajoSeleccionado()) {
                <button type="button" pButton icon="pi pi-times" class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                  severity="danger" pTooltip="Limpiar centro de trabajo" (click)="limpiarCentroTrabajo()"></button>
              }
            </div>
          </div>

          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Fecha Inicial Programación</label>
            <p-datepicker
              [(ngModel)]="fechaInicial"
              dateFormat="yy/mm/dd"
              [showIcon]="true"
              [style]="{ width: '100%' }"
              appendTo="body">
            </p-datepicker>
          </div>

          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Fecha Final Programación</label>
            <p-datepicker
              [(ngModel)]="fechaFinal"
              dateFormat="yy/mm/dd"
              [showIcon]="true"
              [style]="{ width: '100%' }"
              appendTo="body">
            </p-datepicker>
          </div>
        </div>

        <div class="mt-4 flex flex-wrap justify-end sm:justify-start gap-3 w-full">
          <button pButton label="Limpiar" icon="pi pi-eraser" class="p-button-outlined" severity="secondary" (click)="limpiarFiltros()"></button>
          <button pButton label="Buscar" icon="pi pi-search" (click)="buscar()"></button>
        </div>
      </div>

      @if (mensaje()) {
        <div class="p-3 mt-4 rounded" [ngClass]="mensajeClass()">
          {{ mensaje() }}
        </div>
      }

      <div class="mt-4">
        <div class="md:hidden space-y-3">
          @if (!loading() && items().length === 0) {
            <div class="text-center p-4 text-gray-500 border border-gray-200 dark:border-gray-700 rounded-lg">
              No se encontraron resultados para los filtros enviados.
            </div>
          }

          @for (item of items(); track $index) {
            <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900">
              <div class="grid grid-cols-1 gap-2 text-sm">
                <div><span class="text-gray-500">Tarea:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.tarea || '-' }}</span></div>
                <div><span class="text-gray-500">Consultor:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.asesor || '-' }}</span></div>
                <div><span class="text-gray-500">Tipo de Asesor:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.dsTipo || '-' }}</span></div>
                <div><span class="text-gray-500">Fecha envío informe:</span> <span class="text-gray-800 dark:text-gray-200">{{ formatFechaEnvio(item.feenvio) }}</span></div>
                <div><span class="text-gray-500">Tipo de informe:</span> <span class="text-gray-800 dark:text-gray-200">{{ tipoInformeLabel(item.snparcial) }}</span></div>
                <div><span class="text-gray-500">Oficina:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.dsoficina || '-' }}</span></div>
                <div><span class="text-gray-500">Empresa:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.nomempresa || '-' }}</span></div>
                <div><span class="text-gray-500">Centro Trabajo:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.centrotrabajo || '-' }}</span></div>
              </div>
              <div class="mt-3">
                <p-button
                  label="Ver archivos"
                  icon="pi pi-download"
                  severity="secondary"
                  [outlined]="true"
                  size="small"
                  styleClass="w-full min-h-[44px]"
                  [disabled]="!puedeDescargar(item) || descargandoDocumento()"
                  (onClick)="descargarArchivo(item)">
                </p-button>
              </div>
            </div>
          }
        </div>

        <div class="hidden md:block">
        <p-table
          [resizableColumns]="true"
          columnResizeMode="expand"
          [value]="items()"
          [loading]="loading()"
          responsiveLayout="scroll"
          styleClass="tabla-informacion-empresa p-datatable-sm p-datatable-striped p-datatable-gridlines" [scrollable]="true" [tableStyle]="{ 'min-width': '72rem' }">
          <ng-template pTemplate="header">
            <tr>
              <th pResizableColumn><span class="grid-header-text" title="Tarea">Tarea</span></th>
              <th pResizableColumn><span class="grid-header-text" title="Consultor">Consultor</span></th>
              <th pResizableColumn><span class="grid-header-text" title="Tipo de Asesor">Tipo de Asesor</span></th>
              <th pResizableColumn><span class="grid-header-text" title="Fecha envio informe">Fecha envio informe</span></th>
              <th pResizableColumn><span class="grid-header-text" title="Tipo de informe">Tipo de informe</span></th>
              <th pResizableColumn><span class="grid-header-text" title="Oficina">Oficina</span></th>
              <th pResizableColumn><span class="grid-header-text" title="Empresa">Empresa</span></th>
              <th pResizableColumn><span class="grid-header-text" title="Centro Trabajo">Centro Trabajo</span></th>
              <th pResizableColumn class="text-center">Ver Archivos</th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-item>
            <tr>
              <td><span class="grid-cell-text" [attr.title]="item.tarea || '-'">{{ item.tarea || '-' }}</span></td>
              <td><span class="grid-cell-text" [attr.title]="item.asesor || '-'">{{ item.asesor || '-' }}</span></td>
              <td><span class="grid-cell-text" [attr.title]="item.dsTipo || '-'">{{ item.dsTipo || '-' }}</span></td>
              <td>{{ formatFechaEnvio(item.feenvio) }}</td>
              <td>{{ tipoInformeLabel(item.snparcial) }}</td>
              <td><span class="grid-cell-text" [attr.title]="item.dsoficina || '-'">{{ item.dsoficina || '-' }}</span></td>
              <td><span class="grid-cell-text" [attr.title]="item.nomempresa || '-'">{{ item.nomempresa || '-' }}</span></td>
              <td><span class="grid-cell-text" [attr.title]="item.centrotrabajo || '-'">{{ item.centrotrabajo || '-' }}</span></td>
              <td class="text-center">
                <button
                  pButton
                  icon="pi pi-download"
                  class="p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                  pTooltip="Ver archivos"
                  [disabled]="!puedeDescargar(item) || descargandoDocumento()"
                  (click)="descargarArchivo(item)">
                </button>
              </td>
            </tr>
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="9" class="text-center p-6 text-gray-500">
                No se encontraron resultados para los filtros enviados.
              </td>
            </tr>
          </ng-template>
        </p-table>
        </div>
      </div>

      <div class="mt-4 flex justify-end">
        <p-paginator
          [rows]="rows()"
          [first]="first()"
          [totalRecords]="totalRecords()"
          [rowsPerPageOptions]="[10, 20, 50]"
          [showCurrentPageReport]="true"
          currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
          dropdownAppendTo="body"
          appendTo="body"
          (onPageChange)="onPageChange($event)">
        </p-paginator>
      </div>

      @if (mostrarBuscadorTarea) {
        <app-busqueda-tarea-dialog
          [titulo]="'Búsqueda de Tarea'"
          (onSelect)="onTareaSeleccionada($event)"
          (onClose)="mostrarBuscadorTarea = false">
        </app-busqueda-tarea-dialog>
      }

      @if (mostrarBuscadorClasificacion) {
        <app-busqueda-clasificacion-dialog
          (onSelect)="onClasificacionSeleccionada($event)"
          (onClose)="mostrarBuscadorClasificacion = false">
        </app-busqueda-clasificacion-dialog>
      }

      @if (mostrarBuscadorCentroTrabajo) {
        <app-busqueda-centro-trabajo-dialog
          [titulo]="'Búsqueda de Centro de Trabajo'"
          [nmconsecutivoBeneficiario]="idBeneficiario()"
          [nombreBeneficiario]="nombreCliente()"
          [cargarAlAbrir]="true"
          (onSelect)="onCentroTrabajoSeleccionado($event)"
          (onClose)="mostrarBuscadorCentroTrabajo = false">
        </app-busqueda-centro-trabajo-dialog>
      }

      @if (mostrarBuscadorConsultor) {
        <app-asesor-search
          (onSelect)="seleccionarConsultor($event)"
          (onClose)="cerrarBusquedaConsultor()" />
      }
    </div>
  `
})
export class InformacionEmpresaDetalleInformesEntregadosComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly informacionEmpresaRepository = inject(InformacionEmpresaRepository);
  private readonly oficinaRepository = inject(OficinaRepository);
  private readonly usuarioState = inject(UsuarioStateService);
  private readonly documentoP8Service = inject(DocumentoP8Service);
  private readonly messageService = inject(MessageService);

  readonly items = signal<InformacionEmpresaInfDetalle[]>([]);
  readonly loading = signal(false);
  readonly totalRecords = signal(0);
  readonly first = signal(0);
  readonly rows = signal(10);
  readonly oficinas = signal<OpcionOficina[]>([]);
  readonly tiposInforme = signal<OpcionCatalogo[]>(TIPO_INFORME_OPTIONS);
  readonly mensaje = signal('');
  readonly mensajeTipo = signal<'info' | 'warning' | 'error'>('info');
  readonly descargandoDocumento = signal(false);
  readonly mensajeDescarga = signal('Descargando archivo...');

  filtroOficina: number | null = null;
  filtroTipoInforme: number | null = null;
  filtroCumplimiento = '';
  fechaInicial: Date | null = null;
  fechaFinal: Date | null = null;
  private oficinaSesion: number | null = null;

  mostrarBuscadorTarea = false;
  readonly tareaSeleccionada = signal<TareaSeleccionada | null>(null);

  mostrarBuscadorClasificacion = false;
  readonly clasificacionSeleccionada = signal<ClasificacionSeleccionada | null>(null);

  mostrarBuscadorCentroTrabajo = false;
  readonly centroTrabajoSeleccionado = signal<CentroTrabajoSeleccionado | null>(null);

  mostrarBuscadorConsultor = false;
  readonly consultorSeleccionado = signal<AsesorSeleccionado | null>(null);

  ngOnInit(): void {
    void this.inicializar();
  }

  private async inicializar(): Promise<void> {
    await this.precargarSesionYOficinas();
    this.precargarConsultorDesdeContexto();
    this.precargarCumplimientoDesdeContexto();
    this.precargarFechasPorAnio();
    await this.buscar();
  }

  readonly queryParamsRegreso = computed(() => {
    const query = { ...this.route.snapshot.queryParams } as Record<string, unknown>;
    delete query['cdproductoSeleccionado'];
    delete query['dsproductoSeleccionado'];
    delete query['cdoficinaSeleccionada'];
    delete query['dsoficinaSeleccionada'];
    return query;
  });

  readonly queryParamsPrincipal = computed(() => {
    const query = { ...this.route.snapshot.queryParams } as Record<string, unknown>;
    delete query['cdproductoSeleccionado'];
    delete query['dsproductoSeleccionado'];
    delete query['cdoficinaSeleccionada'];
    delete query['dsoficinaSeleccionada'];
    delete query['anioSeleccionado'];
    delete query['cumplimientoSeleccionado'];
    return query;
  });

  readonly nombreCliente = computed(
    () => this.route.snapshot.queryParamMap.get('nombreBeneficiario') ?? 'Sin cliente'
  );

  readonly nombreConsultor = computed(
    () => this.route.snapshot.queryParamMap.get('nombreConsultor') ?? 'Sin consultor'
  );

  readonly anio = computed(() => this.route.snapshot.queryParamMap.get('anioSeleccionado'));

  readonly cumplimiento = computed(
    () => this.route.snapshot.queryParamMap.get('cumplimientoSeleccionado')
  );

  readonly cdproducto = computed(
    () => this.route.snapshot.queryParamMap.get('cdproductoSeleccionado')
  );

  readonly dsproducto = computed(
    () => this.route.snapshot.queryParamMap.get('dsproductoSeleccionado')
  );

  readonly productoContexto = computed(() => {
    const codigo = this.cdproducto()?.trim();
    const descripcion = this.dsproducto()?.trim();
    if (codigo && descripcion) {
      return `${codigo} - ${descripcion}`;
    }
    return codigo || descripcion || '-';
  });

  readonly cdoficina = computed(
    () => this.route.snapshot.queryParamMap.get('cdoficinaSeleccionada')
  );

  readonly idBeneficiario = computed(() =>
    this.toNumber(this.route.snapshot.queryParamMap.get('nmconsecutivoBeneficiario'))
  );

  readonly idProducto = computed(() => this.toNumber(this.cdproducto()));

  readonly cumplimientoReadonly = computed(() => Boolean(this.cumplimiento()));

  readonly consultorSeleccionadoLabel = computed(() => {
    const consultor = this.consultorSeleccionado();
    if (!consultor) {
      return this.route.snapshot.queryParamMap.get('nombreConsultor') ?? '';
    }
    return [consultor.dsnombre1, consultor.dsnombre2, consultor.dsapellido1, consultor.dsapellido2]
      .filter(Boolean)
      .join(' ')
      .trim();
  });

  readonly tareaSeleccionadaLabel = computed(() => {
    const tarea = this.tareaSeleccionada();
    return tarea ? `${tarea.cdservicio} - ${tarea.dsservicio}` : '';
  });

  readonly clasificacionSeleccionadaLabel = computed(() => {
    const clasificacion = this.clasificacionSeleccionada();
    return clasificacion ? `${clasificacion.cdclasificacion} - ${clasificacion.dsclasificacion}` : '';
  });

  readonly centroTrabajoSeleccionadoLabel = computed(() => {
    const centro = this.centroTrabajoSeleccionado();
    return centro ? centro.descripcionCompleta : '';
  });

  readonly mensajeClass = computed(() => {
    if (this.mensajeTipo() === 'error') {
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200';
    }
    if (this.mensajeTipo() === 'warning') {
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200';
    }
    return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200';
  });

  async buscar(): Promise<void> {
    this.first.set(0);
    await this.consultar(0, this.rows());
  }

  async onPageChange(event: PaginatorState): Promise<void> {
    const first = event.first ?? this.first();
    const rows = event.rows ?? this.rows();
    this.first.set(first);
    this.rows.set(rows);
    await this.consultar(first, rows);
  }

  onTareaSeleccionada(tarea: TareaSeleccionada): void {
    this.tareaSeleccionada.set(tarea);
    this.mostrarBuscadorTarea = false;
  }

  onClasificacionSeleccionada(clasificacion: ClasificacionSeleccionada): void {
    this.clasificacionSeleccionada.set(clasificacion);
    this.mostrarBuscadorClasificacion = false;
  }

  limpiarTarea(): void {
    this.tareaSeleccionada.set(null);
  }

  limpiarClasificacion(): void {
    this.clasificacionSeleccionada.set(null);
  }

  abrirBusquedaCentroTrabajo(): void {
    if (!this.idBeneficiario()) {
      this.mensajeTipo.set('warning');
      this.mensaje.set('Debes seleccionar un cliente para buscar centro de trabajo.');
      return;
    }
    this.mostrarBuscadorCentroTrabajo = true;
  }

  onCentroTrabajoSeleccionado(centro: CentroTrabajoSeleccionado): void {
    this.centroTrabajoSeleccionado.set(centro);
    this.mostrarBuscadorCentroTrabajo = false;
  }

  limpiarCentroTrabajo(): void {
    this.centroTrabajoSeleccionado.set(null);
  }

  seleccionarConsultor(consultor: AsesorSeleccionado): void {
    this.consultorSeleccionado.set(consultor);
    this.mostrarBuscadorConsultor = false;
  }

  cerrarBusquedaConsultor(): void {
    this.mostrarBuscadorConsultor = false;
  }

  limpiarConsultor(): void {
    this.consultorSeleccionado.set(null);
  }

  onCumplimientoInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const valorNumerico = input.value.replace(/\D/g, '').slice(0, 11);
    this.filtroCumplimiento = valorNumerico;
    input.value = valorNumerico;
  }

  limpiarFiltros(): void {
    this.tareaSeleccionada.set(null);
    this.clasificacionSeleccionada.set(null);
    this.filtroTipoInforme = null;
    this.precargarCumplimientoDesdeContexto();
    this.centroTrabajoSeleccionado.set(null);
    this.precargarConsultorDesdeContexto();
    this.filtroOficina = this.oficinaSesion;
    this.precargarFechasPorAnio();
    this.items.set([]);
    this.totalRecords.set(0);
    this.first.set(0);
    this.mensaje.set('');
  }

  tipoInformeLabel(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }
    if (value === '1' || value.toUpperCase() === 'FINAL' || value.toUpperCase() === 'N') {
      return 'FINAL';
    }
    if (value === '2' || value.toUpperCase() === 'PARCIAL' || value.toUpperCase() === 'S') {
      return 'PARCIAL';
    }
    return value;
  }

  formatFechaEnvio(value?: string | null): string {
    if (!value) {
      return '-';
    }

    const fecha = new Date(value);
    if (Number.isNaN(fecha.getTime())) {
      return value;
    }

    const year = fecha.getFullYear();
    const month = `${fecha.getMonth() + 1}`.padStart(2, '0');
    const day = `${fecha.getDate()}`.padStart(2, '0');
    const hours = `${fecha.getHours()}`.padStart(2, '0');
    const minutes = `${fecha.getMinutes()}`.padStart(2, '0');

    return `${year}/${month}/${day} ${hours}:${minutes}`;
  }

  puedeDescargar(item: InformacionEmpresaInfDetalle): boolean {
    const fuenteIngresa = `${item.cdfuenteIngresa ?? ''}`.trim();
    if (fuenteIngresa === FUENTE_INGRESA_PENDIENTE) {
      return item.nminforme !== null && item.nminforme !== undefined;
    }
    return fuenteIngresa.length > 0;
  }

  async descargarArchivo(item: InformacionEmpresaInfDetalle): Promise<void> {
    const fuenteIngresa = `${item.cdfuenteIngresa ?? ''}`.trim();
    const nombreArchivo = `informe_${item.nminforme ?? 'detalle'}.pdf`;

    if (fuenteIngresa !== FUENTE_INGRESA_PENDIENTE) {
      if (!fuenteIngresa) {
        this.mostrarMensaje('warn', 'Sin archivo', 'El registro no tiene identificador de documento para descarga.');
        return;
      }

      this.mensajeDescarga.set('Descargando informe...');
      this.descargandoDocumento.set(true);

      try {
        await this.documentoP8Service.descargar(fuenteIngresa, nombreArchivo);
        this.mostrarMensaje('success', 'Descarga iniciada', 'El informe se está descargando.');
      } catch (error) {
        const mensaje = error instanceof Error ? error.message : 'No fue posible descargar el informe.';
        this.mostrarMensaje('error', 'Error al descargar', mensaje);
      } finally {
        this.descargandoDocumento.set(false);
      }
      return;
    }

    if (!item.nminforme) {
      this.mostrarMensaje('warn', 'Sin informe', 'No se encontró el número de informe para consultar en P8.');
      return;
    }

    this.mensajeDescarga.set('Consultando documento en P8...');
    this.descargandoDocumento.set(true);

    try {
      const resultados = await this.documentoP8Service.consultarDocumentos({
        top: 5,
        className: CLASS_NAME_P8,
        query: `NMNroRadicado='${item.nminforme}'`,
        order: ORDER_P8
      });

      const idDocumento = (resultados.find((resultado) => Boolean(resultado.id?.trim()))?.id ?? '').trim();
      if (!idDocumento) {
        this.mostrarMensaje('warn', 'Sin archivo', 'No se encontró documento en P8.');
        return;
      }

      this.mensajeDescarga.set('Descargando informe...');
      await this.documentoP8Service.descargar(idDocumento, nombreArchivo);
      this.mostrarMensaje('success', 'Descarga iniciada', 'El informe se está descargando.');
    } catch (error) {
      const mensaje = error instanceof Error ? error.message : 'No fue posible descargar el informe.';
      this.mostrarMensaje('error', 'Error al descargar', mensaje);
    } finally {
      this.descargandoDocumento.set(false);
    }
  }

  private async precargarSesionYOficinas(): Promise<void> {
    const [usuario, oficinasData] = await Promise.all([
      this.usuarioState.cargar(),
      this.oficinaRepository.obtenerOficinas()
    ]);

    this.oficinas.set(
      oficinasData.map((oficina) => ({
        value: oficina.nmconsecutivo,
        label: oficina.dsoficina
      }))
    );

    const oficinaUsuario = usuario?.nmconsecOficina ?? null;
    const oficinaContexto = this.toNumber(this.cdoficina());
    this.oficinaSesion = oficinaUsuario;
    this.filtroOficina = oficinaContexto ?? oficinaUsuario;
  }

  private precargarConsultorDesdeContexto(): void {
    const nombreConsultor = this.route.snapshot.queryParamMap.get('nombreConsultor');
    const cdpersona = this.toNumber(this.route.snapshot.queryParamMap.get('nmconsecutivoConsultor'));

    if (!nombreConsultor || !cdpersona) {
      this.consultorSeleccionado.set(null);
      return;
    }

    const partes = nombreConsultor.split(' ').filter(Boolean);
    this.consultorSeleccionado.set({
      cdpersona,
      dsnombre1: partes[0] ?? '',
      dsnombre2: partes[1] ?? '',
      dsapellido1: partes[2] ?? '',
      dsapellido2: partes[3] ?? ''
    } as AsesorSeleccionado);
  }

  private precargarFechasPorAnio(): void {
    const anio = this.toNumber(this.anio());
    if (!anio) {
      this.fechaInicial = null;
      this.fechaFinal = null;
      return;
    }
    this.fechaInicial = new Date(anio, 0, 1);
    this.fechaFinal = new Date(anio, 11, 31);
  }

  private precargarCumplimientoDesdeContexto(): void {
    this.filtroCumplimiento = (this.cumplimiento() ?? '').replace(/\D/g, '').slice(0, 11);
  }

  private async consultar(first: number, rows: number): Promise<void> {
    this.loading.set(true);
    this.mensaje.set('');

    try {
      const filtro: InformacionEmpresaInfDetalleFiltro = {
        inutarea: this.tareaSeleccionada()?.nmconsecutivo,
        inuclasificacion: this.clasificacionSeleccionada()
          ? Number(this.clasificacionSeleccionada()?.cdclasificacion)
          : undefined,
        inutipocita: this.filtroTipoInforme ?? undefined,
        inuasesor: this.consultorSeleccionado()?.cdpersona ?? undefined,
        inuoficina: this.filtroOficina ?? undefined,
        inucentrotrabajo: this.centroTrabajoSeleccionado()?.nmconsecutivo,
        inubeneficiario: this.idBeneficiario() ?? undefined,
        inuproducto: this.idProducto() ?? undefined,
        idafeini: this.formatDate(this.fechaInicial),
        idafefin: this.formatDate(this.fechaFinal),
        inuCumplimiento: this.filtroCumplimiento ? Number(this.filtroCumplimiento) : undefined,
        inudesde: first + 1,
        inuhasta: first + rows
      };

      const resultado = await this.informacionEmpresaRepository.listarDetalleInformesEntregados(filtro);
      this.items.set(resultado);
      this.totalRecords.set(resultado[0]?.totalregistro ?? 0);
      if (resultado.length === 0) {
        this.mensajeTipo.set('info');
        this.mensaje.set('La consulta no retornó registros.');
      }
    } catch {
      this.items.set([]);
      this.totalRecords.set(0);
      this.mensajeTipo.set('error');
      this.mensaje.set('Error consultando detalle de informes entregados.');
    } finally {
      this.loading.set(false);
    }
  }

  private mostrarMensaje(severity: 'success' | 'info' | 'warn' | 'error', summary: string, detail: string): void {
    this.messageService.add({ severity, summary, detail });
  }

  private formatDate(value: Date | null): string | undefined {
    if (!value) {
      return undefined;
    }
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toNumber(value: string | null): number | null {
    if (!value) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}